import type { Session } from "../../common/repl-console";
import { isWorking, type Run } from "../../common/run-output";
import type { Scope } from "../../common/variables-panel";
import type { Language } from "./engine";

// Blank is not wrong here and there is nothing to ask for: the panel says which of the several nothings this one is.
export function runMessage(run: Run, scope: Scope | null): string {
  if (isWorking(run)) return "Variables are read when the run ends.";
  if (scope) return "The script defined no variables.";
  if (run.state === "stopped") return "The run was stopped before its variables could be read.";
  if (outOfMemory(run)) return "The engine ran out of memory, so the variables could not be read.";
  return "Nothing has run yet.";
}

// The same several nothings, told apart at a prompt: a session that has ended is one whose names went with it.
export function sessionMessage(run: Run, scope: Scope | null, session: Session): string {
  if (isWorking(run)) return "Variables are read when the line returns.";
  if (scope) return "The session has bound no names.";
  if (session.entries.length > 0) {
    return `The engine ${
      outOfMemory(run) ? "ran out of memory" : "was stopped"
    }, and the session's names went with it.`;
  }
  return "Nothing has been entered yet.";
}

export type Mode = "script" | "repl";

export const COULD_NOT_START = "The engine could not be started.";

// Out here because the panel memoises on it: an object written into the element would be a new one on every render.
// A routine is something somebody wrote down to call rather than a value they are watching, which is why it waits.
export const FUNCTIONS = { label: "Functions", one: "name", many: "names" };

// What a JavaScript prompt says it is waiting for: the start of something, or the rest of what was started.
export const MARKS = { prompt: ">", continued: "..." };

// Said of the block that was interrupted, where the panel says the same of the names: one note apiece, not one twice.
export const STOPPED_NOTE = "Stopped, and the session's names went with the engine.";

// What the status says of a run that used the heap up, since the engine it ran on is let go of rather than asked again.
export const OUT_OF_MEMORY = "Out of memory";
export const OUT_OF_MEMORY_NOTE = "Out of memory, and the session's names went with the engine.";

function outOfMemory(run: Run): boolean {
  return run.state === "failed" && run.message === OUT_OF_MEMORY;
}

export const LANGUAGE_NAMES: Record<Language, string> = {
  javascript: "JavaScript",
  typescript: "TypeScript",
};

// The language and its built-ins, and nothing else: there is no module to import, no network to reach and no file
